import { type ITableSortStates } from "entity/job/JobPriceSummaryPrint/JobPriceSummaryPrint";
import { type ITableSortState } from "entity/job/JobPriceSummaryPrint/JPSSelectionOptionsPrintSection/JPSSelectionOptionsPrintSection";

/**
 * Serializes the JPS per-grid sort state into print-URL query params and back, so the sort a
 * builder applies on the on-screen JPS carries into the separate print window (opened via
 * window.open, which can't share in-memory state).
 *
 * Flat grids each encode as one param: `?selectionOptions=approvalDate:descend`. A grid with no
 * active order is omitted, so a default (unsorted) JPS produces no sort params and print renders
 * in default order.
 *
 * The per-allowance srAllowances map encodes one param per allowance, keyed by id:
 * `?srAllowance.123=totalWithTax:descend`.
 */

/** Flat (single-table) keys of ITableSortStates — excludes the per-allowance `srAllowances` map. */
type FlatSortKey = Exclude<keyof ITableSortStates, "srAllowances">;

const FLAT_SORT_KEYS: readonly FlatSortKey[] = [
    "selections",
    "changeOrders",
    "payments",
    "selectionOptions",
    "budgetDifference", // carry the on-screen Budget difference sort to the print page
];

const VALUE_SEPARATOR = ":";
/** Query-param prefix for a single allowance's sort, e.g. `srAllowance.123`. */
const ALLOWANCE_KEY_PREFIX = "srAllowance.";

// Takes the active order explicitly (never undefined) so the encoded value is type-safe; callers
// only format after guarding on `sort.order`.
const formatSortValue = (field: string, order: "ascend" | "descend"): string =>
    `${field}${VALUE_SEPARATOR}${order}`;

export const encodeJpsSortStates = (sortStates: ITableSortStates): URLSearchParams => {
    const params = new URLSearchParams();
    for (const key of FLAT_SORT_KEYS) {
        const sort = sortStates[key];
        // Only encode grids with an active order; field alone (order undefined) means default.
        if (sort?.order) {
            params.set(key, formatSortValue(sort.field, sort.order));
        }
    }
    for (const [allowanceId, sort] of Object.entries(sortStates.srAllowances ?? {})) {
        if (sort?.order) {
            params.set(
                `${ALLOWANCE_KEY_PREFIX}${allowanceId}`,
                formatSortValue(sort.field, sort.order),
            );
        }
    }
    return params;
};

const parseSortValue = (raw: string | null): ITableSortState | undefined => {
    if (!raw) {
        return undefined;
    }
    const separatorIndex = raw.indexOf(VALUE_SEPARATOR);
    if (separatorIndex < 0) {
        return undefined;
    }
    const field = raw.slice(0, separatorIndex);
    const order = raw.slice(separatorIndex + 1);
    if (!field || (order !== "ascend" && order !== "descend")) {
        return undefined;
    }
    return { field, order };
};

export const parseJpsSortStates = (search: string): ITableSortStates => {
    const params = new URLSearchParams(search);
    const sortStates: ITableSortStates = {};
    for (const key of FLAT_SORT_KEYS) {
        const sort = parseSortValue(params.get(key));
        if (sort) {
            sortStates[key] = sort;
        }
    }
    for (const [param, rawValue] of params.entries()) {
        if (!param.startsWith(ALLOWANCE_KEY_PREFIX)) {
            continue;
        }
        const allowanceId = Number(param.slice(ALLOWANCE_KEY_PREFIX.length));
        const sort = parseSortValue(rawValue);
        if (Number.isFinite(allowanceId) && sort) {
            sortStates.srAllowances = { ...sortStates.srAllowances, [allowanceId]: sort };
        }
    }
    return sortStates;
};
